import { PropType, toRaw, ref, Ref, watch, SetupContext } from 'vue';
import { useShareData } from '../../core/SharedData';
import type { PlainObject } from '../../core/types';
import {
  getLayout,
  saveLayout,
  deleteLayout
} from '../../core/LayoutPersistent';
import { isArray, isEqual, toInteger } from 'lodash-es';

// 读取保存的条件
export function loadCondition(
  panel: string,
  field: string,
  def?: any,
  check?: (v: any) => boolean
) {
  return getLayout('search', ['persist', panel, field], def, check);
}

// 保存搜索条件
export function saveCondition(panel: string, field: string, value: any) {
  if (value === undefined) {
    deleteLayout('search', ['persist', panel, field]);
    return;
  }
  saveLayout('search', ['persist', panel, field], value);
}

// 读取搜索历史
export function loadHistory(panel: string, field: string): string[] {
  return getLayout('search', ['history', panel, field], [], isArray);
}

export function saveHistory(panel: string, field: string, value: string[]) {
  saveLayout('search', ['history', panel, field], value);
}

// 组件共用属性
type BasePropType<V> = {
  name: string;
  label: string;
  defaultValue?: V;
  sendDefault?: boolean;
  persist?: boolean;
  map?: Record<number, any>;
  rawId?: boolean;
  unreset?: boolean; // 点击清除，不重置值
  defaultHide?: boolean; // 默认隐藏
  unset?: boolean; // 不允许通过 setParams 方法重新赋值
};

// setup共用的返回字段
type BaseSetupReturn<V> = {
  visible: Ref<boolean | undefined>;
  input: Ref<V | undefined>;
  reset: () => void;
  submit: () => any;
};

// setup 扩展逻辑的参数
type HookParams<V> = {
  input: Ref<V | undefined>;
  submit: () => void;
};

// 构造properites
export function buildSearchFieldProps<V, P extends PlainObject>(
  valueCtor: () => V,
  extProps: P
) {
  return {
    ...extProps,

    name: { type: String, required: true },
    label: { type: String, required: true },
    defaultValue: {
      type: valueCtor as PropType<V>,
      required: false,
      default: undefined
    },

    sendDefault: { type: Boolean }, // 发送请求，默认值也发送
    persist: { type: Boolean }, // 是否保护上次的搜索值

    // 对value值映射转换
    map: {
      type: Object as PropType<Record<number, any>>,
      default: undefined
    },
    rawId: { type: Boolean }, // 保持id类型不变, 默认会转成number
    unreset: { type: Boolean }, // 点击清除，不重置值
    defaultHide: { type: Boolean } // 默认隐藏
  } as const;
}

// setup 通用函数
export function setupSearchField<
  V,
  P extends BasePropType<V>,
  R extends PlainObject
>(
  valueCtor: () => V,
  props: P,
  ctx: SetupContext,
  hook: (more: HookParams<V>) => R,
  params?: {
    sendDefault?: boolean; // 查询时是否发送默认值到服务
    defaultTranslate?: (v: V | undefined) => V | undefined; // 默认值预处理
    translate?: (v: V | undefined) => V | undefined; // 实际发送数据的转换
    rawId?: boolean; // 是否保持原始id, 即不转成number
    persistHook?: (v: V | undefined) => void;
  }
): BaseSetupReturn<V> & R {
  // searchPanel名
  const panelName = useShareData('search', 'name');
  // 搜索字段名
  const name = props.name;

  let persistValue = props.persist
    ? (loadCondition(panelName, name) as V)
    : undefined;

  const translateFun = params?.defaultTranslate;
  const defaultValue0 = props.defaultValue;
  const defaultValue = translateFun
    ? translateFun(defaultValue0)
    : defaultValue0 !== undefined
    ? defaultValue0
    : valueCtor();

  const initValue = persistValue ?? defaultValue;
  let input = ref(initValue) as Ref<V | undefined>;

  const searchParams = useShareData('search', 'params').$(name);

  // 执行查询操作
  const submitFunction = useShareData('search', 'submit');
  const submit = () => submitFunction(false);

  const setupParams = { input, submit };
  const exts = hook?.(setupParams);
  input = setupParams.input;

  // 值转换
  const translate = params?.translate ? params.translate : (v: any) => v;
  const convert = props.map ? (v: any) => props.map![v] : translate;

  const sendDefault = props.sendDefault || params?.sendDefault;
  const rawId = params?.rawId === undefined ? props.rawId : params?.rawId;

  // 组件value变更时，更新到SearchPanel查询条件中
  watch(
    input,
    (newValue) => {
      if (sendDefault || !isEqual(newValue, defaultValue)) {
        searchParams(convert(newValue));
      } else {
        searchParams(undefined);
      }
    },
    { immediate: true }
  );

  const layout = useShareData('search', 'layout');
  // 是否隐藏
  const visible = ref<boolean>(false);

  // 逆向转换映射
  const from = props.map
    ? (v: any) => {
        const raw = toRaw(v);
        for (const [key, val] of Object.entries(props.map!)) {
          if (isEqual(val, raw)) {
            return rawId ? key : toInteger(key);
          }
        }
        return undefined;
      }
    : (v: any) => v;

  // 注册字段名
  layout.fieldInterfaceMap.$(name)({
    label: props.label,
    hideNotify: (hidden: boolean) => {
      if (hidden) {
        input.value = undefined;
        visible.value = false;
      } else {
        input.value = initValue;
        visible.value = true;
      }
    },
    set: (val: any) =>
      !props.unset && visible.value && (input.value = from(val)),
    reset: () =>
      !props.unreset && visible.value && (input.value = defaultValue),
    persist: () => {
      if (!props.unreset && visible.value && params?.persistHook) {
        params?.persistHook?.(input.value);
        return;
      }

      if (
        props.unset ||
        !props.persist ||
        !visible.value ||
        isEqual(input.value, persistValue)
      ) {
        return;
      }

      const isDefault = isEqual(input.value, defaultValue);
      if (isDefault && persistValue === undefined) {
        return;
      }

      persistValue = input.value;

      // 保存配置
      saveCondition(panelName, name, isDefault ? undefined : persistValue);
    },
    // 默认是否隐藏
    defaultHide: props.defaultHide
  });

  const reset = () => (input.value = defaultValue);

  return {
    visible,
    input,
    reset,
    submit,
    ...exts
  } as BaseSetupReturn<V> & R;
}

/////////////////////////////////////////////////////////////////////
// 值为array的组件
// 组件共用属性
type BaseArrayPropType<V> = {
  name: string[];
  label: string;
  defaultValue?: V[];
  sendDefault?: boolean;
  //persist?: boolean;
  rawId?: boolean;

  unreset?: boolean; // 点击清除不重置
  defaultHide?: boolean; // 默认隐藏
  unset?: boolean; // 不允许通过 setParams 方法重新赋值
};

export function buildArraySearchFieldProps<V, P>(
  _valueCtor: () => V,
  extProps: P
) {
  return {
    ...extProps,
    name: { type: Array as PropType<string[]>, required: true },
    label: { type: String, required: true },
    defaultValue: {
      type: Array as PropType<V[]>,
      required: false,
      default: undefined
    },

    sendDefault: { type: Boolean },

    rawId: { type: Boolean }, // 保持id类型不变, 默认会转成number
    unreset: { type: Boolean }, // 点击清除，不重置值
    defaultHide: { type: Boolean } // 默认是否隐藏
  } as const;
}

// setup 通用函数
export function setupArraySearchField<
  V,
  P extends BaseArrayPropType<V>,
  R extends PlainObject
>(
  _valueCtor: () => V,
  props: P,
  ctx: SetupContext,
  hook: (more: HookParams<V[]>) => R,
  params?: {
    sendDefault?: boolean; // 查询时是否发送默认值到服务
    defaultTranslate?: (v: V[] | undefined) => V[] | undefined; // 默认值预处理
    valueTranslate?: (v: V[] | undefined, ext: R) => V[] | undefined; // 组件值转换预处理
  }
): BaseSetupReturn<V[]> & R {
  const translateFun =
    params?.defaultTranslate ||
    ((v: V[] | undefined) => (v !== undefined ? v : []));

  const defaultValue0 = props.defaultValue;
  const defaultValue = translateFun(defaultValue0);
  let input = ref(defaultValue) as Ref<V[] | undefined>;

  // daterange 则多个key
  const name = props.name;
  const searchParams = [] as any[];
  for (let i = 0; i < name.length; i++) {
    searchParams[i] = useShareData('search', 'params').$(name[i]);
  }

  // 执行查询操作
  const submit = useShareData('search', 'submit');

  const setupParams = { input, submit };
  const exts = hook?.(setupParams);
  input = setupParams.input;

  const sendDefault = props.sendDefault || params?.sendDefault;
  const valueTranslate = params?.valueTranslate || ((v: V[] | undefined) => v);
  // 组件value变更时，更新到SearchPanel查询条件中
  watch(
    input,
    (newValue) => {
      const translatedValue = valueTranslate(newValue, exts);

      for (let i = 0; i < name.length; i++) {
        if (sendDefault || !equalArrayItem(translatedValue, defaultValue, i)) {
          searchParams[i](translatedValue?.[i]);
        } else {
          searchParams[i](undefined);
        }
      }
    },
    { immediate: true }
  );

  // 只注册一个
  const firstName = name[0];
  const secondName = name[1];

  const layout = useShareData('search', 'layout');
  // 是否隐藏
  const visible = ref<boolean>(false);
  // 注册字段名
  layout.fieldInterfaceMap.$(firstName)({
    label: props.label,
    hideNotify: (hidden: boolean) => {
      if (hidden) {
        input.value = undefined;
        visible.value = false;
      } else {
        input.value = defaultValue;
        visible.value = true;
      }
    },
    set: (val: V | undefined, ps: PlainObject) => {
      !props.unset &&
        visible.value &&
        (input.value = translateFun([val, ps[secondName]]));
    },
    reset: () =>
      !props.unreset && visible.value && (input.value = defaultValue),

    defaultHide: props.defaultHide // 默认是否隐藏
  });

  const reset = () => (input.value = defaultValue);

  return {
    visible,
    input,
    reset,
    submit,
    ...exts
  } as BaseSetupReturn<V[]> & R;
}

function equalArrayItem(v1: any, v2: any, i: number) {
  if (v1 === v2) {
    return true;
  }

  if (!v1 || v2) {
    return false;
  }

  if (v1 || !v2) {
    return false;
  }

  return isEqual(v1[i], v2[i]);
}
